import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";
import { privateTemplateSchema } from "./template-read";

const name = z
  .string()
  .min(1)
  .refine(
    (value) => Array.from(value).length <= 100,
    "Name exceeds 100 characters",
  );
const headers = z
  .array(
    z
      .string()
      .min(1)
      .refine((value) => !value.includes(","), "Header cannot contain a comma"),
  )
  .refine(
    (value) => Array.from(value.join(",")).length <= 500,
    "Encoded headers exceed 500 characters",
  );
const instructions = z
  .string()
  .refine(
    (value) => new TextEncoder().encode(value).length <= 16777215,
    "Instructions exceed storage limit",
  );
// A template needs instructions or at least one section (clinical lead,
// 29/10/2026): instructions alone are a complete template; the note then
// follows the structure the instructions describe.
export const templateHasContent = (value: {
  headers?: readonly string[] | null;
  optionalText?: string | null;
}): boolean => (value.headers?.length ?? 0) > 0 || !!value.optionalText?.trim();
export const privateTemplateDefinitionSchema = z
  .strictObject({
    name,
    headers,
    optionalText: instructions.default(""),
    isDefault: z.boolean().default(false),
  })
  .refine(templateHasContent, "Add instructions or at least one section");
export const privateTemplateCreateSchema = z.union([
  privateTemplateDefinitionSchema,
  z.strictObject({ templatePublicId: publicIdSchema }),
]);
export const privateTemplateUpdateSchema = z
  .strictObject({
    name: name.optional(),
    headers: headers.optional(),
    optionalText: instructions.nullable().optional(),
    isDefault: z.boolean().optional(),
    baseVersion: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .optional(),
  })
  .refine(
    (value) =>
      Object.entries(value).some(
        ([key, value]) => key !== "baseVersion" && value !== undefined,
      ),
    "An edit is required",
  );
export type PrivateTemplateDefinition = z.output<
  typeof privateTemplateDefinitionSchema
>;
export type PrivateTemplateCreate = z.output<
  typeof privateTemplateCreateSchema
>;
export type PrivateTemplateUpdate = z.output<
  typeof privateTemplateUpdateSchema
>;
const write = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  NOT_FOUND: { message: "Not Found" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  SERVICE_UNAVAILABLE: { message: "Authentication unavailable" },
});
const params = z.strictObject({
  public_id: publicIdSchema,
});
export const templateWriteContract = {
  createTemplate: write
    .input(
      z.object({
        query: z.strictObject({}).default({}),
        body: privateTemplateCreateSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/templates",
        successStatus: 201,
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(privateTemplateSchema),
  updateTemplate: write
    .errors({ CONFLICT: { message: "Template changed since proposal" } })
    .input(
      z.object({
        params,
        query: z.strictObject({}).default({}),
        body: privateTemplateUpdateSchema,
      }),
    )
    .meta(
      openapi({
        method: "PATCH",
        path: "/api/v2/templates/{public_id}",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(privateTemplateSchema),
  deleteTemplate: write
    .errors({ CONFLICT: { message: "Template cannot be deleted" } })
    .input(
      z.object({
        params,
        query: z.strictObject({}).default({}),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "DELETE",
        path: "/api/v2/templates/{public_id}",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(z.object({ public_id: publicIdSchema })),
};
